import { CustomLayerInterface, CustomRenderMethodInput, Map as MaplibreMap } from 'maplibre-gl';
import fragmentSource from './stars.fragment.glsl?raw';
import vertexSource from './stars.vertex.glsl?raw';
import { selectStarmapTarget, uploadStarCubemap, type StarmapProvider } from './starmap-cubemap';

// Compute Greenwich Mean Sidereal Time in radians
function computeGMST(date: Date): number {
    const JD = date.getTime() / 86400000.0 + 2440587.5;
    const T = (JD - 2451545.0) / 36525.0;
    const gmstArcSec = 67310.54841 + (8640184.812866 + (0.093104 - 6.2e-6 * T) * T) * T;
    const gmstDeg = (gmstArcSec / 3600.0) % 360.0;
    return (((gmstDeg * Math.PI) / 180.0) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
}

interface MaplibreTransform {
    modelViewProjectionMatrix?: ArrayLike<number>;
}

function invertMat4(out: Float32Array, a: Float32Array): boolean {
    const a00 = a[0],  a01 = a[1],  a02 = a[2],  a03 = a[3];
    const a10 = a[4],  a11 = a[5],  a12 = a[6],  a13 = a[7];
    const a20 = a[8],  a21 = a[9],  a22 = a[10], a23 = a[11];
    const a30 = a[12], a31 = a[13], a32 = a[14], a33 = a[15];

    const b00 = a00 * a11 - a01 * a10;
    const b01 = a00 * a12 - a02 * a10;
    const b02 = a00 * a13 - a03 * a10;
    const b03 = a01 * a12 - a02 * a11;
    const b04 = a01 * a13 - a03 * a11;
    const b05 = a02 * a13 - a03 * a12;
    const b06 = a20 * a31 - a21 * a30;
    const b07 = a20 * a32 - a22 * a30;
    const b08 = a20 * a33 - a23 * a30;
    const b09 = a21 * a32 - a22 * a31;
    const b10 = a21 * a33 - a23 * a31;
    const b11 = a22 * a33 - a23 * a32;

    let det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
    if (!det) {
        return false;
    }
    det = 1.0 / det;

    out[0]  = (a11 * b11 - a12 * b10 + a13 * b09) * det;
    out[1]  = (a02 * b10 - a01 * b11 - a03 * b09) * det;
    out[2]  = (a31 * b05 - a32 * b04 + a33 * b03) * det;
    out[3]  = (a22 * b04 - a21 * b05 - a23 * b03) * det;
    out[4]  = (a12 * b08 - a10 * b11 - a13 * b07) * det;
    out[5]  = (a00 * b11 - a02 * b08 + a03 * b07) * det;
    out[6]  = (a32 * b02 - a30 * b05 - a33 * b01) * det;
    out[7]  = (a20 * b05 - a22 * b02 + a23 * b01) * det;
    out[8]  = (a10 * b10 - a11 * b08 + a13 * b06) * det;
    out[9]  = (a01 * b08 - a00 * b10 - a03 * b06) * det;
    out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * det;
    out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * det;
    out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * det;
    out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * det;
    out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * det;
    out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * det;

    return true;
}

export class StarsLayer implements CustomLayerInterface {
    readonly type = 'custom';
    readonly renderingMode = '3d';
    readonly id: string;

    private provider: StarmapProvider;
    private map: MaplibreMap | null = null;
    private gl: WebGL2RenderingContext | null = null;

    private program: WebGLProgram | null = null;
    private aPos: GLint = 0;
    private vao: WebGLVertexArrayObject | null = null;
    private vertexBuffer: WebGLBuffer | null = null;
    private indexBuffer: WebGLBuffer | null = null;
    private texture: WebGLTexture | null = null;
    private textureReady = false;

    // Bumped on every load. An in-flight load whose generation no longer
    // matches must discard its result, or switching variants quickly can land
    // an older sky on top of a newer one.
    private loadGeneration = 0;

    private opacity: number;
    private raOffset: number;

    private static readonly FADE_IN_DURATION_MS = 300;
    private fadeInStart: number | null = null;

    constructor(id: string, provider: StarmapProvider, opacity = 1.0, initialRaOffset = 0) {
        this.id = id;
        this.provider = provider;
        this.opacity = opacity;
        this.raOffset = initialRaOffset;
    }

    onAdd(map: MaplibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
        if (!(gl instanceof WebGL2RenderingContext)) {
            console.error('StarsLayer requires WebGL2');
            return;
        }

        this.map = map;
        this.gl = gl;

        this.program = this.buildProgram(gl);
        if (!this.program) {
            return;
        }

        this.aPos = gl.getAttribLocation(this.program, 'a_pos');
        this.setupQuad(gl);
        this.setupVao(gl);
        void this.loadTexture(gl);
    }

    render(gl: WebGLRenderingContext | WebGL2RenderingContext, args: CustomRenderMethodInput): void {
        if (args.shaderData.variantName !== 'globe') {
            return;
        }
        if (!this.program || !this.texture || !this.textureReady) {
            return;
        }

        const invMatrix = this.getInverseMatrix();
        if (!invMatrix) {
            return;
        }

        const fade = this.getFadeFactor();

        gl.useProgram(this.program);
        gl.uniformMatrix4fv(gl.getUniformLocation(this.program, 'u_inv_matrix'), false, invMatrix);
        gl.uniform1f(gl.getUniformLocation(this.program, 'u_opacity'), this.opacity * fade);
        gl.uniform1f(gl.getUniformLocation(this.program, 'u_ra_offset'), this.raOffset);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_CUBE_MAP, this.texture);
        gl.uniform1i(gl.getUniformLocation(this.program, 'u_stars'), 0);

        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        // Don't write depth — globe tiles rendered after us must pass their own
        // depth tests and paint correctly over the star background.
        gl.depthMask(false);

        (gl as WebGL2RenderingContext).bindVertexArray(this.vao);
        gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
        (gl as WebGL2RenderingContext).bindVertexArray(null);

        gl.depthMask(true);
        gl.disable(gl.BLEND);
    }

    onRemove(_map: MaplibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
        this.loadGeneration++;

        if (this.vao) {
            (gl as WebGL2RenderingContext).deleteVertexArray(this.vao);
            this.vao = null;
        }
        if (this.program) {
            gl.deleteProgram(this.program);
        }
        if (this.vertexBuffer) {
            gl.deleteBuffer(this.vertexBuffer);
        }
        if (this.indexBuffer) {
            gl.deleteBuffer(this.indexBuffer);
        }
        if (this.texture) {
            gl.deleteTexture(this.texture);
        }

        this.program = null;
        this.vertexBuffer = null;
        this.indexBuffer = null;
        this.texture = null;
        this.textureReady = false;
        this.fadeInStart = null;
        this.map = null;
        this.gl = null;
    }

    setProvider(provider: StarmapProvider): void {
        this.provider = provider;
        if (this.gl) {
            void this.loadTexture(this.gl);
        }
    }

    setOpacity(opacity: number): void {
        this.opacity = Math.max(0, Math.min(1, opacity));
        this.map?.triggerRepaint();
    }

    setTime(date: Date): void {
        this.raOffset = computeGMST(date);
        this.map?.triggerRepaint();
    }

    setRaOffset(radians: number): void {
        this.raOffset = radians;
        this.map?.triggerRepaint();
    }

    private async loadTexture(gl: WebGL2RenderingContext): Promise<void> {
        const generation = ++this.loadGeneration;
        const target = selectStarmapTarget(gl);
        this.textureReady = false;
        this.fadeInStart = null;

        try {
            const cube = await this.provider(target);
            if (generation !== this.loadGeneration) {
                return;
            }

            const { texture, levelsUploaded, busyMs, frames } = await uploadStarCubemap(gl, cube, target, {
                isAborted: () => generation !== this.loadGeneration
            });

            if (generation !== this.loadGeneration) {
                gl.deleteTexture(texture);
                return;
            }

            if (this.texture) {
                gl.deleteTexture(this.texture);
            }
            this.texture = texture;
            this.textureReady = true;
            this.fadeInStart = performance.now();
            this.map?.triggerRepaint();

            console.debug(
                `StarsLayer: ${cube.width}px x6, ${levelsUploaded} mips, ${target.name}, ` +
                    `upload ${busyMs.toFixed(0)}ms over ${frames} frames`
            );
        } catch (e) {
            if (generation === this.loadGeneration) {
                console.error('StarsLayer: failed to load starmap', e);
            }
        }
    }

    private buildProgram(gl: WebGL2RenderingContext): WebGLProgram | null {
        const vert = this.compileShader(gl, gl.VERTEX_SHADER, vertexSource, 'vertex');
        const frag = this.compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource, 'fragment');
        if (!vert || !frag) {
            return null;
        }

        const prog = gl.createProgram();
        if (!prog) {
            return null;
        }

        gl.attachShader(prog, vert);
        gl.attachShader(prog, frag);
        gl.linkProgram(prog);

        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
            console.error('StarsLayer: program link error:', gl.getProgramInfoLog(prog));
            gl.deleteProgram(prog);
            return null;
        }

        gl.deleteShader(vert);
        gl.deleteShader(frag);
        return prog;
    }

    private compileShader(
        gl: WebGL2RenderingContext,
        type: number,
        source: string,
        debugName: string
    ): WebGLShader | null {
        const shader = gl.createShader(type);
        if (!shader) {
            return null;
        }

        gl.shaderSource(shader, source);
        gl.compileShader(shader);

        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            console.error(`StarsLayer: ${debugName} compile error:`, gl.getShaderInfoLog(shader));
            gl.deleteShader(shader);
            return null;
        }

        return shader;
    }

    private setupQuad(gl: WebGL2RenderingContext): void {
        const vertices = new Float32Array([-1.0, -1.0, 1.0, -1.0, 1.0, 1.0, -1.0, 1.0]);
        const indices = new Uint16Array([0, 1, 2, 0, 2, 3]);

        this.vertexBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
        gl.bindBuffer(gl.ARRAY_BUFFER, null);

        this.indexBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
    }

    private setupVao(gl: WebGL2RenderingContext): void {
        this.vao = gl.createVertexArray();
        gl.bindVertexArray(this.vao);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
        gl.enableVertexAttribArray(this.aPos);
        gl.vertexAttribPointer(this.aPos, 2, gl.FLOAT, false, 0, 0);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);

        gl.bindVertexArray(null);
        gl.bindBuffer(gl.ARRAY_BUFFER, null);
    }

    private getFadeFactor(): number {
        if (this.fadeInStart === null) {
            return 1;
        }

        const elapsed = performance.now() - this.fadeInStart;
        if (elapsed >= StarsLayer.FADE_IN_DURATION_MS) {
            this.fadeInStart = null;
            return 1;
        }

        this.map?.triggerRepaint();

        const t = Math.max(0, Math.min(1, elapsed / StarsLayer.FADE_IN_DURATION_MS));
        return t * t * (3 - 2 * t);
    }

    private getInverseMatrix(): Float32Array | null {
        const transform = this.map?._camera.transform;
        if (!transform?.modelViewProjectionMatrix) {
            return null;
        }

        const mvp = new Float32Array(16);
        for (let i = 0; i < 16; i++) {
            mvp[i] = transform.modelViewProjectionMatrix[i];
        }

        const inv = new Float32Array(16);
        if (!invertMat4(inv, mvp)) {
            return null;
        }
        return inv;
    }
}


